<template>
  <v-dialog
    v-model="show"
    :width="width"
    :min-height="minHeight"
    :max-height="maxHeight"
    @input="closeDialog"
    :persistent="loading"
    scrollable
  >
    <v-sheet class="nav-wrapper rounded-l-lg" v-if="!hideNav">
      <v-btn
        x-large
        icon
        :disabled="!showBtnPrev"
        @click="navAttendances(1)"
        color="primary"
        title="Atendimento Anterior"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
    </v-sheet>
    <v-fade-transition hide-on-leave>
      <v-sheet v-if="!attendance" class="pa-8">
        <div v-if="!!error">
          <p>Não foi possível carregar o resumo do atendimento: {{ error }}</p>
        </div>
        <div v-else class="d-flex flex-column fill-height gap-2 justify-center align-center">
          <v-progress-circular indeterminate />
          <p>Carregando dados do atendimento...</p>
        </div>
      </v-sheet>
      <v-fade-transition v-else hide-on-leave>
        <v-card v-show="!loading" id="printable-area" class="elevation-0 rounded-0">
          <div v-if="printing" v-html="headerHtml" />
          <v-card-title class="noprint d-flex column-gap-5 align-center pb-10 text-h5">
            <span>Resumo do atendimento de {{ attendance.disease.name }}</span>
            <select-field
              class="caption"
              style="height: 55%"
              :items="templates"
              :itemText="(item) => item.name"
              returnObject
              placeholder="Modelos para Impressão"
              v-model="selectedTemplate"
            />
            <v-btn class="noprint" icon color="primary" title="Imprimir resumo" :loading="printing" @click="print"
              ><v-icon>mdi-printer</v-icon></v-btn
            >
          </v-card-title>
          <v-card-subtitle>
            <div class="subheader">
              <div class="item subheader__item subheader__item--patient">
                <label class="label subheader__label">Paciente</label>
                <p class="value subheader__value">
                  {{ attendance.patient.socialName || attendance.patient.name }},
                  {{ patientAgeDuringAttendance }}
                </p>
              </div>
              <div class="item subheader__item">
                <label class="label subheader__label">Data de atendimento</label>
                <p class="value subheader__value">
                  {{ $moment(attendanceToShow.startedAt).format('DD/MM/YYYY [às] HH:mm') }}
                </p>
              </div>
              <div class="item subheader__item subheader__item--patient">
                <label class="label subheader__label">Peso</label>
                <p class="value subheader__value">{{ attendanceToShow.weightKg }} kg</p>
              </div>
            </div>
          </v-card-subtitle>
          <v-card-text>
            <section class="section section--summary">
              <header class="section__header">Evolução e conduta</header>
              <p class="value value--paragraph mb-0" v-for="(p, index) in noteParagraphs" :key="`note-p${index}`">
                {{ p || '&nbsp;' }}
              </p>
            </section>

            <section class="section section--summary nobreakinside" v-if="summaryItems.length > 0">
              <header class="section__header">Métricas</header>
              <div class="section__content">
                <div class="item summary__item" v-for="item in summaryItems" :key="`summary-${item.key}`">
                  <label class="label summary__label">{{ item.text }}</label>
                  <p class="value summary__value">{{ item?.value }}</p>
                </div>
              </div>
            </section>

            <template v-for="phase in joinedPhases">
              <section
                class="section section--summary"
                :key="`section-${phase.key}`"
                v-if="(phase.overviewGenerator(phase.data, attendance) || []).length > 0"
              >
                <p
                  class="value value--paragraph mb-0"
                  v-for="(p, index) in phase.overviewGenerator(phase.data, attendance)"
                  :key="`${phase.key}-p${index}`"
                  v-html="p || '&nbsp;'"
                ></p>
              </section>
            </template>

            <section class="section section--summary" v-if="showHypothesis">
              <header class="section__header">Hipótese</header>
              <p
                class="value value--paragraph mb-0"
                v-for="(p, index) in hypothesisParagraphs"
                :key="`hypothesis-p${index}`"
              >
                {{ p || '&nbsp;' }}
              </p>
            </section>

            <template v-for="phase in phases">
              <section
                class="section section--summary"
                :key="`section-${phase.key}`"
                v-if="(phase.overviewGenerator(phase.data, attendance) || []).length > 0"
              >
                <header class="section__header">{{ phase.name }}</header>
                <p
                  class="value value--paragraph mb-0"
                  v-for="(p, index) in phase.overviewGenerator(phase.data, attendance)"
                  :key="`${phase.key}-p${index}`"
                  v-html="p || '&nbsp;'"
                ></p>
              </section>
            </template>
          </v-card-text>
          <div v-if="printing" v-html="footerHtml" />
        </v-card>
      </v-fade-transition>
    </v-fade-transition>
    <v-sheet class="nav-wrapper rounded-r-lg" v-if="!hideNav">
      <v-btn
        x-large
        icon
        :disabled="!showBtnNext"
        @click="navAttendances(-1)"
        color="primary"
        title="Próximo Atendimento"
      >
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </v-sheet>
  </v-dialog>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
const { mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')

import { getAttendanceConfig } from '@/store/attendance/config/AttendanceConfig'
import SelectField from '../SelectField.vue'

import { DISEASES } from '@/constants/disease'

export default {
  components: { SelectField },
  name: 'AttendanceOverviewDialog',
  props: {
    hideNav: {
      type: Boolean,
      default: false,
    },
    width: {
      type: [String, Number],
      default: '800',
    },
    maxHeight: {
      type: [String, Number],
      default: '80vh',
    },
    minHeight: {
      type: [String, Number],
      default: '20vh',
    },
  },
  computed: {
    headerHtml() {
      if (!!this.selectedTemplate?.templateHeader) {
        return this.selectedTemplate.templateHeader
      }
      return ''
    },
    footerHtml() {
      if (!!this.selectedTemplate?.templateFooter) {
        return this.selectedTemplate.templateFooter
      }
      return ''
    },
    showBtnPrev() {
      return (
        this.attendanceHistory.length > 1 &&
        this.currentIndex !== this.attendanceHistory.length - 1 &&
        this.loading === false
      )
    },
    showBtnNext() {
      return this.attendanceHistory.length > 1 && this.currentIndex !== 0 && this.loading === false
    },
    summaryItems() {
      return (Object.entries(this.attendanceToShow?.summary || {}) || []).map((entry) => ({
        key: entry[0],
        ...entry[1],
      }))
    },
    noteParagraphs() {
      if (!this.attendanceToShow?.note) {
        return []
      }

      return this.attendanceToShow.note.split(/\n/).map((p) => p.trim())
    },
    hypothesisParagraphs() {
      if (!this.attendanceToShow?.hypothesis) {
        return []
      }

      return this.attendanceToShow.hypothesis.split(/\n/).map((p) => p.trim())
    },
    showHypothesis() {
      return this.hypothesisParagraphs.length > 0 && this.attendanceToShow.disease.id === DISEASES.ESPONDILOARTRITIS
    },
    phases() {
      if (!this.attendance || !this.attendanceToShow || !this.attendanceToShow.disease) {
        return []
      }
      const filteredPhases = ['areas_afetadas', 'pasi_bsa', 'dlqi']

      const attendancePhases = getAttendanceConfig(this.attendanceToShow.disease.id)
        .getAllPhases()
        .filter((phase) => !filteredPhases.includes(phase.key) && typeof phase.overviewGenerator === 'function')
        .map((phase) => {
          return {
            key: phase.key,
            name: phase.name === 'Dados específicos' ? 'Histórico da doença' : phase.name,
            data:
              typeof this.attendanceToShow.data === 'string'
                ? JSON.parse(this.attendanceToShow.data)[phase.key]
                : this.attendanceToShow.data[phase.key],
            overviewGenerator: phase.overviewGenerator,
          }
        })

      return attendancePhases
    },

    joinedPhases() {
      if (!this.attendance || !this.attendanceToShow || !this.attendanceToShow.disease) {
        return []
      }

      const filteredPhases = ['areas_afetadas', 'pasi_bsa', 'dlqi']

      const attendancePhases = getAttendanceConfig(this.attendanceToShow.disease.id)
        .getAllPhases()
        .filter((phase) => filteredPhases.includes(phase.key) && typeof phase.overviewGenerator === 'function')
        .map((phase) => ({
          key: phase.key,
          name: phase.name,
          data: this.attendanceToShow.data[phase.key],
          overviewGenerator: phase.overviewGenerator,
        }))

      return attendancePhases
    },

    patientAgeDuringAttendance() {
      const patientBirthday = this.attendance.patient.birthday
      const attendanceDate = this.attendanceToShow.startedAt

      return `${this.$moment(attendanceDate).diff(patientBirthday, 'years')} anos`
    },
  },
  data() {
    return {
      attendanceToShow: [],
      show: false,
      error: '',
      loading: false,
      printing: false,
      templates: [],
      selectedTemplate: null,
      attendanceHistory: [],
      currentIndex: null,
      attendance: null,
    }
  },
  async created() {
    this.templates = (await this.$api.getTemplates()).items
    this.templates = this.templates.map((template) => ({
      id: template.id,
      name: template.name,
      templateHeader: template.header,
      templateFooter: template.footer,
    }))
    if (this.templates.length === 1) {
      this.selectedTemplate = this.templates
    }
  },
  methods: {
    async openDialog(attendanceId, previousAttendances) {
      this.show = true
      await this.loadAttendanceOverview(attendanceId)
      this.attendance = await this.$api.getAttendance(attendanceId)
      if (previousAttendances) {
        this.attendanceHistory = previousAttendances
        if (this.attendanceHistory.length > 1) {
          this.currentIndex = this.attendanceHistory.findIndex((attendance) => attendance.id === attendanceId)
          this.attendanceToShow = await this.$api.getAttendance(this.attendanceHistory[this.currentIndex].id)
        }
      } else {
        this.attendanceToShow = this.attendance
      }
    },
    closeDialog() {
      this.show = false
      this.clearError()
    },
    clearError() {
      this.error = ''
    },
    async loadAttendanceOverview(attendanceId) {
      try {
        this.clearError()
        this.loading = true
      } catch (error) {
        this.error = error
      } finally {
        this.loading = false
      }
    },
    async navAttendances(mod) {
      if (this.currentIndex + mod < this.attendanceHistory.length && this.currentIndex + mod > -1) {
        this.currentIndex = this.currentIndex + mod
        this.loadAttendanceOverview(this.attendanceHistory[this.currentIndex].id)
        this.attendanceToShow = await this.$api.getAttendance(this.attendanceHistory[this.currentIndex].id)
      }
    },
    print() {
      this.printing = true
      const printOptions = {
        styles: [
          'https://fonts.googleapis.com/css?family=Roboto:100,300,400,500,700,900',
          'https://maxcdn.bootstrapcdn.com/bootstrap/4.0.0/css/bootstrap.min.css',
          'https://cdn.jsdelivr.net/npm/vuetify@2.4.0/dist/vuetify.min.css',
          '/styles/printPortrait.css',
          '/styles/printAppointmentOverview.css',
        ],
      }
      this.$nextTick(async () => {
        await this.$htmlToPaper('printable-area', printOptions)
        this.printing = false
      })
    },
  },
}
</script>

<style scoped>
@import '/public/styles/printAppointmentOverview.css';

.nav-wrapper {
  width: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
